import { memo, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FastForward } from "lucide-react";
import { TopBar } from "./top-bar";
import { RetentionBanner } from "./retention-banner";
import { H3PromoBanner } from "@/components/promo/h3-promo-banner";
import { useH3Promo } from "@/hooks/use-h3-promo";

interface MainLayoutProps {
  children: React.ReactNode;
}

export const MainLayout = memo(function MainLayout({
  children,
}: MainLayoutProps) {
  const mainRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const { isAnnounced: h3PromoAnnounced } = useH3Promo();

  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="h-full flex flex-col overflow-hidden bg-background relative">
      <div className="absolute inset-x-0 top-0 z-40">
        <RetentionBanner />
        {h3PromoAnnounced ? (
          <H3PromoBanner />
        ) : (
          <Link
            to="/extend-cartoon"
            className="flex items-center justify-center gap-2 bg-[#b8a5f0] border-b-2 border-[#2a2342] py-1.5 px-4 text-[11px] md:text-[12px] font-extrabold text-[#241d3a] whitespace-nowrap transition-colors hover:bg-[#c8b8f5]"
          >
            <FastForward className="size-3.5 shrink-0" strokeWidth={3} />
            New — Extend videos automatically stitch together for unlimited-length videos
          </Link>
        )}
        <TopBar />
      </div>
      <main
        ref={mainRef}
        className="flex-1 overflow-y-auto overflow-x-hidden overscroll-none flex flex-col pt-[var(--navbar-h)]"
        style={{ overscrollBehavior: "none" }}
      >
        <div className="animate-page-in flex-1">
          {children}
        </div>
        <footer className="shrink-0 py-8 px-5 mt-auto safe-bottom">
          <div className="max-w-3xl mx-auto flex items-center justify-center gap-5 flex-wrap text-[12px] font-bold text-[#241d3a]/35">
            <Link to="/terms" className="hover:text-[#241d3a]/60 transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-[#241d3a]/60 transition-colors">Privacy</Link>
            <Link to="/refund-policy" className="hover:text-[#241d3a]/60 transition-colors">Refunds</Link>
            <Link to="/support" className="hover:text-[#241d3a]/60 transition-colors">Support</Link>
            <Link to="/affiliates" className="hover:text-[#241d3a]/60 transition-colors">Affiliate</Link>
            <span className="text-[#241d3a]/35">CartoonTale</span>
          </div>
        </footer>
      </main>
    </div>
  );
});
